'use client';

import styles from '@/assets/icons.module.scss';
import { ClearIcon } from '@/components/icons/clear-icon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import { buildUrlWithParams } from '@/utils/url-params';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { redirect } from 'next/navigation';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import globalSearchFormStyles from './global-search-form.module.scss';

type GlobalSearchFormProps = {
  isClosing?: boolean;
  onClickOutsideAction?: () => void;
};

export const GlobalSearchForm = ({ isClosing, onClickOutsideAction }: GlobalSearchFormProps): React.JSX.Element => {
  const searchWebsitePanelRef = useRef<HTMLDivElement>(null);
  const formSchema = z.object({
    search: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      search: '',
    },
    resolver: zodResolver(formSchema),
  });

  const search = (values: z.infer<typeof formSchema>): void => {
    onClickOutsideAction?.();
    redirect(buildUrlWithParams('/Search', { text: values.search }));
  };

  return (
    <Card ref={searchWebsitePanelRef}
      className={clsx(
        'fixed z-105 md:absolute top-0 md:-top-1 right-0 md:-right-2 left-0 md:left-auto md:p-0',
        isClosing ? globalSearchFormStyles.closing : globalSearchFormStyles['global-search-container']
      )}
    >
      <CardContent>
        <form className={clsx(globalSearchFormStyles.row, globalSearchFormStyles['global-search-form'])}
          id="searchWebsiteForm"
          onSubmit={form.handleSubmit(search)}
        >
          <Controller control={form.control}
            name="search"
            render={({ field, fieldState }) =>
              <Field>
                <FieldLabel className="md:sr-only"
                  dataTest="label-search-website"
                  error={!!fieldState.error}
                  htmlFor="searchWebsite"
                >
                  Search
                </FieldLabel>
                <InputGroup>
                  <InputGroupInput aria-invalid={fieldState.invalid}
                    data-test="input-search-website"
                    id="searchWebsite"
                    {...field}
                  />
                  <InputGroupAddon align="inline-end"
                    className="pr-0"
                  >
                    <InputGroupButton aria-label="Clear search"
                      type="button"
                      onClick={() => form.setValue('search', '')}
                    >
                      <ClearIcon aria-hidden="true"
                        className={cn(
                          'icon no-hover-effect dont-grow mx-1 cursor-pointer',
                          styles['icon-clear'],
                          'opacity-0 group-focus-within/input-group:opacity-100 transition-opacity'
                        )}
                        viewBox="0 0 16 16"
                      />
                    </InputGroupButton>
                  </InputGroupAddon>
                  <InputGroupAddon align="inline-end"
                    className="pr-0"
                  >
                    <InputGroupButton className="hidden size-9 font-semibold text-white bg-cnk-red
                      md:inline-flex md:rounded md:rounded-l-none md:px-2"
                    data-test="button-search-website"
                    type="submit"
                    >
                      <span className="md:sr-only">Search</span>
                      <span className="sr-only md:not-sr-only">
                        <svg className="icon no-hover-effect !fill-white"
                          role="img"
                          viewBox="0 0 20 20"
                        >
                          <use href="#search" />
                        </svg>
                      </span>
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </Field>
            }
          />

          <Button className="md:hidden"
            data-test="button-search-website-mobile"
            type="submit"
          >
            Search
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
